import React, { useState, useEffect, useRef } from 'react';
import '../Editor.css'; // Import the new shared editor styles

const ButtonCommandList = ({ 
  buttons = {}, 
  commands = {}, 
  selectedButton, 
  handleButtonSelect, 
  visibleNpcs = {}, 
  collapsedNpcs = {},
  setCollapsedNpcs
}) => {
  // State to track collapsed dialogues
  const [collapsedDialogues, setCollapsedDialogues] = useState({});
  // Ref to track auto-scrolling to selected button
  const selectedButtonRef = useRef(null);
  
  // Auto-scroll to the selected button when it changes
  useEffect(() => {
    if (selectedButton && selectedButtonRef.current) {
      // Smoothly scroll the selected button into view
      selectedButtonRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  }, [selectedButton]);
  
  // Function to toggle NPC collapse state
  const toggleNpcCollapse = (npc) => {
    setCollapsedNpcs(prev => ({
      ...prev,
      [npc]: !prev[npc]
    }));
  };

  // Function to toggle dialogue collapse state
  const toggleDialogueCollapse = (npc, dialogue) => {
    const key = `${npc}:${dialogue}`;
    setCollapsedDialogues(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="editor-container button-command-list-container">
      <div className="button-command-content">
        {Object.entries(buttons).length === 0 ? (
          <div className="empty-state">No button commands found. Add NPCs, dialogues, and buttons first.</div>
        ) : (
          Object.entries(buttons).map(([npc, dialogues]) => {
            if (!dialogues || (visibleNpcs && !visibleNpcs[npc])) return null;
            
            // Check if this NPC is collapsed
            const isNpcCollapsed = collapsedNpcs[npc] || false;
            
            return (
              <div key={npc} className="npc-section">
                {/* NPC header with collapse toggle */}
                <div 
                  className="npc-header"
                  onClick={() => toggleNpcCollapse(npc)}
                >
                  <span>{npc}</span>
                  <span>{isNpcCollapsed ? '▼' : '▲'}</span>
                </div>
                
                {/* Only render dialogues if not collapsed */}
                {!isNpcCollapsed && (
                  <div className="npc-content">
                    {Object.entries(dialogues).map(([dialogue, buttonArray]) => {
                      if (!buttonArray) return null;
                      
                      // Create a unique key for this dialogue
                      const dialogueKey = `${npc}:${dialogue}`;
                      // Check if this dialogue is collapsed
                      const isDialogueCollapsed = collapsedDialogues[dialogueKey] || false;
                      
                      return (
                        <div key={dialogueKey} className="dialogue-section">
                          <div 
                            className="dialogue-item"
                            onClick={() => toggleDialogueCollapse(npc, dialogue)}
                          >
                            <div className="dialogue-header">
                              <span className="dialogue-title text-wrap">
                                {dialogue.length > 30 ? 
                                  `${dialogue.substring(0, 30)}...` : 
                                  dialogue}
                              </span>
                              <span className="toggle-icon">
                                {isDialogueCollapsed ? '▼' : '▲'}
                              </span>
                            </div>
                          </div>
                          
                          {/* Render buttons only if dialogue is not collapsed */}
                          {!isDialogueCollapsed && (
                            <div className="button-commands">
                              {buttonArray.map((button, index) => {
                                if (!button || button === '$placeholder_button') return null;
                                
                                // Check if this button is selected
                                const isSelected = selectedButton && 
                                  selectedButton.npc === npc && 
                                  selectedButton.dialogue === dialogue && 
                                  selectedButton.button === button;
                                
                                return (
                                  <div 
                                    key={index} 
                                    className={`button-item ${isSelected ? 'selected' : ''}`}
                                    ref={isSelected ? selectedButtonRef : null}
                                  >
                                    <div className="button-header">
                                      <span className="button-name text-wrap">{button}</span>
                                      <button 
                                        className={`editor-button ${isSelected ? 'selected' : ''}`}
                                        onClick={() => handleButtonSelect(npc, dialogue, button)}
                                      >
                                        {isSelected ? 'Selected' : 'Select'}
                                      </button>
                                    </div>
                                    
                                    {/* Display commands for this button */}
                                    {commands[npc] && 
                                     commands[npc][dialogue] && 
                                     commands[npc][dialogue][button] && (
                                      <div className="command-list">
                                        {commands[npc][dialogue][button].map((cmd, cmdIndex) => (
                                          <div key={cmdIndex} className="command-item text-wrap">
                                            {cmd}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ButtonCommandList;